import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { darkModeClass } from '../../utils/cn';
import styles from './Contact.module.css';

const Contact = () => {
  const { isDarkMode } = useTheme();
  const { data, t } = useLanguage();
  const personalData = data.personal;
  const { email, phone, location } = personalData;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isLoading, setIsLoading] = useState(false);

  const contactApiUrl = process.env.REACT_APP_CONTACT_API_URL || '/api/send-email';

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      setStatus({ type: 'error', message: t('contact.fillRequired') });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setStatus({ type: 'error', message: t('contact.validEmail') });
      return;
    }

    setIsLoading(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch(contactApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject || t('contact.formTitle'),
          message: formData.message
        })
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        console.error('API Error Response:', result);
        throw new Error(result.error || result.details?.message || 'Unable to send message');
      }

      setStatus({ type: 'success', message: t('contact.success') });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Brevo email request failed:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      setStatus({ type: 'error', message: t('contact.failure') });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="contact" className={`${styles.contact} ${darkModeClass(styles, isDarkMode)}`}>
      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>{t('contact.title')}</h2>
          <p className={styles.sectionSubtitle}>{t('contact.subtitle')}</p>
        </div>
        
        <div className={styles.contactContainer}>
          <div className={styles.contactInfo}>
            <h3 className={styles.contactInfoTitle}>{t('contact.info')}</h3>
            <p className={styles.contactInfoText}>{t('contact.intro')}</p>
            
            <div className={styles.contactDetails}>
              <div className={styles.contactItem}>
                <div className={styles.contactIcon}>
                  <i className="fas fa-envelope"></i>
                </div>
                <div className={styles.contactText}>
                  <h4>{t('contact.email')}</h4>
                  <a href={`mailto:${email}`}>{email}</a>
                </div>
              </div>
              
              <div className={styles.contactItem}>
                <div className={styles.contactIcon}>
                  <i className="fas fa-phone"></i>
                </div>
                <div className={styles.contactText}>
                  <h4>{t('contact.phone')}</h4>
                  <a href={`tel:${phone}`}>{phone}</a>
                </div>
              </div>
              
              <div className={styles.contactItem}>
                <div className={styles.contactIcon}>
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <div className={styles.contactText}>
                  <h4>{t('contact.location')}</h4>
                  <p>{location}</p>
                </div>
              </div>
            </div>
            
            <div className={styles.socialContact}>
              <h4>{t('contact.connect')}</h4>
              <div className={styles.socialIcons}>
                {personalData.socialLinks.map((link) => (
                  <a 
                    key={link.url}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.socialIcon}
                    aria-label={link.name}
                  >
                    <i className={`fab ${link.icon}`}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>
          
          <div className={styles.contactForm}>
            <h3 className={styles.formTitle}>{t('contact.formTitle')}</h3>
            
            {status.message && (
              <div id="contact-error" role="alert" className={`${styles.statusMessage} ${status.type === 'error' ? styles.error : styles.success}`}>
                <i className={`fas ${status.type === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle'}`}></i>
                {status.message}
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="name">{t('contact.name')} *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                    aria-describedby={status.type === 'error' ? 'contact-error' : undefined}
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label htmlFor="email">{t('contact.email')} *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                    aria-describedby={status.type === 'error' ? 'contact-error' : undefined}
                  />
                </div>
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="subject">{t('contact.subject')}</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </div>
              
              <div className={styles.formGroup}>
                <label htmlFor="message">{t('contact.message')} *</label>
                <textarea
                  id="message"
                  name="message"
                  rows="6"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  aria-describedby={status.type === 'error' ? 'contact-error' : undefined}
                ></textarea>
              </div>
              
              <button 
                type="submit" 
                className={styles.submitButton}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> {t('contact.sending')}
                  </>
                ) : (
                  <>
                    {t('contact.send')} <i className="fas fa-paper-plane"></i>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;